import { computed, effect, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { Params } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthSessionDto, UnityAuthContext, UnitySessionStatus, UserPreferencesClient } from '@unity/clients';
import { NavigationService } from '@unity/services/navigation.service';
import { UnityRouteUrlTrees } from '@unity/utils/types';
import { firstValueFrom } from 'rxjs';

export const SessionTokenKey = 'access_token';
export const ImpersonatorSessionTokenKey = 'impersonator_access_token';
export const CurrentAgencyKey = 'current_agency';
const defaultReturnUrl: UnityRouteUrlTrees = ['/agreement'];

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly navigationService = inject(NavigationService);
  private readonly jwtHelper = inject(JwtHelperService);
  private readonly userPreferencesClient = inject(UserPreferencesClient);

  private readonly $_impersonatorToken = signal(localStorage.getItem(ImpersonatorSessionTokenKey));
  private readonly $_currentAgencyId = signal<number | null>(this.getAgencyId());
  private readonly $customLoginReturnUrl = signal<UnityRouteUrlTrees | null>(null);

  private parseToken(accessToken: string) {
    const sessionBase = this.jwtHelper.decodeToken<UnityAuthContext>(accessToken);
    if (sessionBase == null) {
      throw new Error('Tried to read a null session');
    }

    return sessionBase;
  }

  private getAgencyId() {
    const stored = localStorage.getItem(CurrentAgencyKey);
    if (stored && stored.trim()) {
      const parsed = parseInt(stored);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    return null;
  }

  private handleSessionResponseRedirect(response: AuthSessionDto): void {
    if ((response.status & UnitySessionStatus.Success) === UnitySessionStatus.Success) {
      const current = this.$current();
      // eslint-disable-next-line @unity-rules/warn-console-log
      console.log(`API VERSION: ${current?.api_version}`);
      this.navigationService.navigate(['/login-select-business-partner']);
    } else if ((response.status & UnitySessionStatus.Failed) === UnitySessionStatus.Failed) {
      this.navigationService.navigate(['/disabled']);
    } else {
      this.navigationService.navigate(['/pending'], { queryParams: { status: response.status } });
    }
  }

  private async getLoginRedirectUrl(): Promise<UnityRouteUrlTrees> {
    const customUrl = this.$customLoginReturnUrl();

    if (customUrl) {
      return customUrl;
    }

    const defaultAgreementId = (await firstValueFrom(this.userPreferencesClient.getMine())).default_agreement_id;
    if (defaultAgreementId) {
      return ['/agreement', defaultAgreementId, 'view'] as UnityRouteUrlTrees;
    } else {
      return defaultReturnUrl;
    }
  }

  // Public signals
  public readonly $token = signal(localStorage.getItem(SessionTokenKey));

  // Initial state is based on the presence of a token, but managed by `login` and `logout` methods thereafter
  public readonly $isLoggedIn = linkedSignal(() => this.$token() !== null);
  public readonly $isImpersonating = computed(() => this.$_impersonatorToken() !== null);
  public readonly $currentAgencyId = computed(() => this.$_currentAgencyId());

  public readonly $current = computed(() => {
    const token = this.$token();
    if (!token) {
      return;
    }
    return this.parseToken(token);
  });

  public readonly $isExpired = computed(() => {
    const loggedIn = this.$isLoggedIn();
    const token = this.$token();
    return loggedIn && this.jwtHelper.isTokenExpired(token);
  });

  public readonly $isActive = computed(() => {
    const current = this.$current();
    if (!current) {
      return false;
    }
    return (current?.status & UnitySessionStatus.Success) === UnitySessionStatus.Success;
  });

  public readonly $sessionRoles = computed(() => this.$current()?.user_roles ?? []);
  public readonly $user = computed(() => {
    const currentSession = this.$current();
    if (!currentSession) {
      throw new Error('Tried to read current user but no valid session was found.');
    }
    return currentSession.user;
  });
  public readonly $hasNoValidAgencies = computed(() => this.$sessionRoles()?.length === 0);
  public readonly $isSingleAgency = computed(() => this.$sessionRoles()?.length === 1);

  public readonly $currentAgencyRole = computed(() => {
    const agencyId = this.$currentAgencyId();
    const sessionRoles = this.$sessionRoles();
    const active = sessionRoles?.find(a => a.agency_id === agencyId);
    if (!active) {
      return null;
    }
    return active;
  });
  public readonly $isInternalUserRole = computed(() => this.$currentAgencyRole()?.is_internal ?? false);
  public readonly $isAdministratorUserRole = computed(() => this.$currentAgencyRole()?.is_administrator ?? false);

  public readonly $environmentName = computed(() => {
    const currentSession = this.$current();
    return currentSession?.environment_name;
  });

  public constructor() {
    // Redirect to agency selection when logged in but no agencyId
    effect(() => {
      const agencyId = this.$_currentAgencyId();
      const loggedIn = this.$isLoggedIn();

      if (loggedIn && agencyId === null) {
        this.navigationService.navigate(['/login-select-business-partner']);
      }
    });

    // Persist session token to storage
    effect(() => {
      const token = this.$token();
      if (!token) {
        localStorage.removeItem(SessionTokenKey);
      } else {
        localStorage.setItem(SessionTokenKey, token);
      }
    });

    // Persist impersonator token to storage
    effect(() => {
      const token = this.$_impersonatorToken();
      if (!token) {
        localStorage.removeItem(ImpersonatorSessionTokenKey);
      } else {
        localStorage.setItem(ImpersonatorSessionTokenKey, token);
      }
    });

    // Persist currentAgencyId to storage
    effect(() => {
      const agencyId = this.$_currentAgencyId();
      if (!agencyId) {
        localStorage.removeItem(CurrentAgencyKey);
      } else {
        localStorage.setItem(CurrentAgencyKey, agencyId.toString());
      }
    });
  }

  /*
  public methods
  */
  public async loginToAgencyAndRedirect(agencyId: number) {
    this.$isLoggedIn.set(true);
    this.$_currentAgencyId.set(agencyId);

    const returnUrl = await this.getLoginRedirectUrl();

    return await this.navigationService.navigate(returnUrl);
  }

  public async maybeRedirectToSingleAgency() {
    const sessionRoles = this.$sessionRoles();
    if (sessionRoles?.length === 1) {
      const role = sessionRoles[0];
      return await this.loginToAgencyAndRedirect(role.agency_id);
    }
    return Promise.resolve(false);
  }

  public handleSessionResponse(response: AuthSessionDto): void {
    if ((response.status & UnitySessionStatus.Success) === UnitySessionStatus.Success) {
      this.$token.set(response.access_token);
    }
    this.handleSessionResponseRedirect(response);
  }

  public handleImpersonationResponse(response: AuthSessionDto): void {
    if ((response.status & UnitySessionStatus.Success) === UnitySessionStatus.Success) {
      const currentToken = this.$token();
      const impersonationToken = response.access_token;

      this.$_impersonatorToken.set(currentToken);
      this.$token.set(impersonationToken);
      this.$_currentAgencyId.set(null);
    }
    this.handleSessionResponseRedirect(response);
  }

  public clean() {
    this.$_impersonatorToken.set(null);
    this.$_currentAgencyId.set(null);
  }

  public endImpersonation() {
    const impersonatorToken = this.$_impersonatorToken();
    this.$token.set(impersonatorToken);
    this.clean();
    this.navigationService.navigate(['/login-select-business-partner']);
  }

  public logout({ returnUrl, queryParams }: { returnUrl?: UnityRouteUrlTrees; queryParams?: Params } | undefined = {}) {
    this.clean();
    this.$token.set(null);
    this.$customLoginReturnUrl.set(returnUrl ?? null);
    this.$isLoggedIn.set(false);
    localStorage.removeItem(SessionTokenKey);
    this.navigationService.navigate(['/login'], { queryParams });
  }
}
